import { 
  Box, 
  Chip, 
  Grid, 
  LinearProgress, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Typography 
} from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { hostname, parseError } from "../../utils";

interface FeatureUsageData {
  feature: string;
  name: string;
  credits: number;
  tokens: number;
}

interface FeatureUsageResponse {
  features: FeatureUsageData[];
  totalCredits: number;
  scope: "company" | "platform";
}

interface FeatureUsageChartProps {
  companyId: string;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
}

/**
 * FeatureUsageChart component displays a pie chart showing credit usage breakdown by feature
 * for the last 30 days. Includes both visual chart and detailed breakdown table.
 */
const FeatureUsageChart: React.FC<FeatureUsageChartProps> = ({
  companyId,
  getToken,
  eventListener,
}) => {
  const [loading, setLoading] = useState(true);
  const [featureData, setFeatureData] = useState<FeatureUsageData[]>([]);
  const [totalCredits, setTotalCredits] = useState(0);
  const [scope, setScope] = useState<"company" | "platform">("company");
  const [error, setError] = useState<string | null>(null);

  const loadFeatureUsage = async () => {
    setLoading(true);
    setError(null);
    try {
      const currentToken = await getToken();
      const response = await axios.get<FeatureUsageResponse>(
        `${hostname}/usage/features/last_30_days`,
        {
          params: { companyId },
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );

      setFeatureData(response.data.features || []);
      setTotalCredits(response.data.totalCredits || 0);
      setScope(response.data.scope || "company");
    } catch (err) {
      const errorMessage = parseError(err);
      setError(errorMessage);
      eventListener({
        type: "SET_ERROR",
        error: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeatureUsage();
  }, [companyId]);

  // Prepare data for PieChart
  const pieData = featureData
    .filter((feature) => feature.credits > 0)
    .map((feature, index) => ({
      id: index,
      value: feature.credits,
      label: feature.name,
    }));

  // Color palette for the pie chart - Material UI inspired colors
  const colors = [
    '#1976d2', // Primary Blue
    '#388e3c', // Green
    '#f57c00', // Orange
    '#7b1fa2', // Purple
    '#d32f2f', // Red
    '#0288d1', // Light Blue
    '#689f38', // Light Green
    '#f9a825', // Amber
    '#c2185b', // Pink
    '#5d4037', // Brown
    '#455a64', // Blue Grey
    '#e64a19', // Deep Orange
    '#512da8', // Deep Purple
  ];

  if (loading) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Feature Usage
        </Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Feature Usage
        </Typography>
        <Typography variant="body2" color="error" gutterBottom>
          Error loading feature usage data: {error}
        </Typography>
      </Box>
    );
  }

  if (featureData.length === 0 || totalCredits === 0) {
    return (
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h6" gutterBottom>
            Feature Usage
          </Typography>
          {scope === "platform" && (
            <Chip
              label="Platform-wide"
              color="primary"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          No usage data available for the last 30 days
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="h6" gutterBottom>
          Feature Usage
        </Typography>
        {scope === "platform" && (
          <Chip
            label="Platform-wide"
            color="primary"
            size="small"
            variant="outlined"
          />
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {scope === "platform"
          ? "Showing feature usage across all companies (last 30 days)"
          : "Showing feature usage for this company (last 30 days)"}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Total credits used: {totalCredits.toFixed(1)}
      </Typography>
      
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={8}>
          <PieChart
            series={[
              {
                data: pieData,
                highlightScope: { faded: 'global', highlighted: 'item' },
                faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
              },
            ]}
            colors={colors}
            height={400}
            slotProps={{
              legend: {
                direction: 'column',
                position: { vertical: 'middle', horizontal: 'right' },
                padding: 0,
              },
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><Typography variant="subtitle2">Feature</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2">Credits</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2">%</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {featureData
                  .filter((feature) => feature.credits > 0)
                  .sort((a, b) => b.credits - a.credits)
                  .map((feature, index) => {
                    const percentage = ((feature.credits / totalCredits) * 100).toFixed(1);
                    return (
                      <TableRow key={feature.feature || index}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: colors[index % colors.length],
                              }}
                            />
                            <Typography variant="body2">{feature.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{feature.credits.toFixed(1)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{percentage}%</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FeatureUsageChart;